import { Container, Sprite, Text, Texture } from "pixi.js";

import { Battery } from "../../../shared/Aiming";
import { Fitout, STARTING_FITOUT } from "../../../shared/Fitout";
import { Cannon, rosterFor } from "../../../shared/Gunnery";
import { CRUISE_SPEED, normaliseAngle, shortestAngle, turnToward } from "../../../shared/Sailing";
import { Sea, SLOPE_UNIT, waveHeightAt, WAVE_HEIGHT, waveSlopeAt } from "../sea/Waves";
import { sailPower } from "../../../shared/Wind";
import { SEA_LIGHT } from "../sea/OceanFilter";
import { buildDeck, Deck, dressEmblems, train, trim } from "./Deck";
import { MuzzleFlashes } from "./MuzzleFlashes";

/**
 * How fast a sail billows, in frames per second — the rate the canvas was drawn for, and the
 * one the dashboard previews it at. Stepped from the scene's clock rather than by `autoUpdate`,
 * so it means frames per second on any machine. See design/Evolution.md.
 */
const SAIL_FPS = 12;
/**
 * How fast the yards come round to a new point of sail, in radians per second. Faster than a hull
 * turns, so the canvas leads the manoeuvre rather than lagging it, but slow enough that the swing
 * is a movement a player can see. See `trimTo`.
 */
const BRACE_RATE = 2;

/**
 * How a hull shows where it is on a wave. All three are scaled by `waveHeight`, so a flat calm
 * leaves boats sitting perfectly still — the sea being quiet has to look like the sea being quiet.
 *
 * **Lift** is scale, because height in a top-down view is size and the eye reads it with no
 * training at all. A couple of percent is enough: more and a hull on a crest starts to read as a
 * bigger *ship* rather than a higher one, which is a lie the whole evolution ladder is built on
 * telling honestly.
 *
 * **Roll** is a small rotation off the heading. It is deliberately smaller than anything a player
 * could mistake for a turn — the wake is what says which way a hull is going, and a rocking boat
 * must not argue with it.
 */
const LIFT = 0.035;
const ROLL = 0.055;

/**
 * The shadow: how dark it gets in a trough and on a crest, and how far it slides out from under
 * the hull down there, as a fraction of the hull's length.
 *
 * It falls **away from `SEA_LIGHT`**, the same quarter the sea itself is lit from, because a
 * shadow disagreeing with the water it is cast on is the one thing on screen that would say none
 * of this is a surface.
 *
 * **Longer in a trough and darker on a crest**, which are not the same axis and were worth
 * separating. Long is geometry: a hull down between waves is lit from further round, so its shadow
 * reaches. Dark is contrast: up in the light a hull throws a crisp shadow, and down in the shade of
 * the wave in front of it there is little light left to block. Drawn the other way round — deepest
 * in the trough — the shadow lands on the darkest water on screen and disappears, which is where
 * this started.
 */
const SHADOW_ALPHA_CREST = 0.34;
const SHADOW_ALPHA_TROUGH = 0.12;
const SHADOW_REACH = 0.09;
/** How much bigger than the hull the soft blob is drawn, so its edge is shadow and not a rim. */
const SHADOW_SPREAD = 1.25;
const SHADOW_COLOR = 0x04263a;
/** Nameplate offset below the hull's centre, as a fraction of that hull's length. */
const NAME_OFFSET_FACTOR = 0.62;
const NAME_SIZE = 15;

/**
 * How an abandoned hull is drawn: washed toward the sea's own cold blue and faded into it.
 *
 * Both, rather than one or the other. Alpha alone would read as a boat that is simply further
 * down in the water, and the tint alone would leave a fully solid ship in an odd colour. Together
 * they say *derelict* at a glance and from the corner of the eye, which is the bar every silhouette
 * in this game is held to — a captain deciding whether to turn and engage should not have to read
 * a label to know they are looking at free plunder. See design/Battle.md.
 *
 * Applied to the boat's root container, so it carries down through the deck, the guns and the
 * nameplate in one assignment — `alpha` and `tint` both multiply through a subtree.
 */
const GHOST_TINT = 0x8fb0bd;
const GHOST_ALPHA = 0.6;

/**
 * A soft round blot, built once and shared by every hull in the sea.
 *
 * Drawn on a canvas rather than loaded, for the reason the wake's marks are: it is a gradient
 * rather than art, nobody would author it, and a spritesheet entry for one radial fade is a file
 * to keep in step for no gain. See design/Water.md on the trail's blobs.
 */
let shadowBlot: Texture | null = null;

function shadowTexture(): Texture {
    if (shadowBlot) return shadowBlot;

    const radius = 64;
    const canvas = document.createElement("canvas");
    canvas.width = radius * 2;
    canvas.height = radius * 2;

    const context = canvas.getContext("2d");
    if (!context) throw new Error("no 2d context to draw a shadow with");

    // Solid in the middle and gone by the rim, with the falloff weighted outwards so the blot has
    // a body rather than being a single soft smear.
    const gradient = context.createRadialGradient(radius, radius, 0, radius, radius, radius);
    gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
    gradient.addColorStop(0.55, "rgba(255, 255, 255, 0.72)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

    context.fillStyle = gradient;
    context.fillRect(0, 0, radius * 2, radius * 2);

    shadowBlot = Texture.from(canvas);

    return shadowBlot;
}

/**
 * A single vessel: whatever ship its `Fitout` describes.
 *
 * It is an **assembly** rather than one sprite — the hull's base frame, the deck furniture laid
 * on it in order, a gun in every filled cannon slot and a sail in every raised one, each at its
 * own rung. [Deck.ts](Deck.ts) builds that; this class owns the parts of a boat that are not the
 * deck: the nameplate, the heading, and the clock the canvas billows on.
 *
 * **A boat can be refitted while it is sailing.** Crossing a milestone and spending the point is
 * a bigger hull or another gun *on the water, immediately* — design/Evolution.md says that is
 * most of the reward, so `fitout` is a setter and not a constructor argument that never moves.
 * Rebuilding the deck is a dozen sprites and happens a handful of times a life.
 *
 * The art faces north — bow at the top of the frame — so a heading of zero points straight up
 * the screen and drives `container.rotation` directly.
 *
 * A boat does not steer itself. Something outside sets `targetHeading` and the boat swings
 * onto it at TURN_RATE; `velocity` is then where it would be going, for whoever is moving
 * the world. A networked boat instead has `heading` assigned outright, because its turning
 * already happened on the server. The swing here is deliberately turbo-blind — TURN_SPEED_FACTOR
 * is `advance()`'s business — because the only scene that lets a boat steer itself is the
 * preview, where nothing boosts. See design/Ships.md.
 *
 * It has no position. It is a view — `MainScene` leaves it at screen centre, `BattleScene`
 * places it from world coordinates it keeps itself. See design/Ships.md.
 */
export class Boat {
    private readonly _container = new Container();
    private readonly _nameplate: Text;

    private _fitout: Fitout;
    private _deck: Deck;
    /**
     * Where this boat's guns have swung, and the roster they swing on. Owned by the boat rather
     * than by the scene because every hull in the sea has one and they are all drawn the same
     * way — the only difference is which aim they are fed, and that is `aimAt`'s argument.
     *
     * It **survives a refit**: a `Battery` files offsets by slot, so a gun already trained on a
     * target stays trained through the frame a point is spent. See shared/Aiming.ts.
     */
    private readonly _battery = new Battery();
    private _roster = rosterFor(STARTING_FITOUT);
    /**
     * The fire out of this ship's guns. A child of the boat rather than of the sea, so a flash
     * rides the hull that made it for the third of a second it lives — see MuzzleFlashes.ts.
     */
    private readonly _flashes = new MuzzleFlashes();

    private _heading = 0;
    /**
     * How far this ship's yards are braced off square, in radians. Eased toward the angle the
     * point of sail asks for rather than snapped to it, so the canvas swings across as the hull
     * comes about instead of teleporting — a crew takes a moment. See `trimTo`.
     */
    private _brace = 0;
    /** The blot under the hull. First child, so everything else on the boat is drawn over it. */
    private readonly _shadow = new Sprite({ texture: shadowTexture(), anchor: 0.5, tint: SHADOW_COLOR, alpha: 0 });
    private _targetHeading = 0;
    private _speed = CRUISE_SPEED;
    private _sailTime = 0;
    /** Whether this hull has been abandoned by its captain. Purely a look — see `ghost`. */
    private _ghost = false;

    public get container(): Container {
        return this._container;
    }

    /** Current heading in radians, 0 = north, growing clockwise. */
    public get heading(): number {
        return this._heading;
    }

    /**
     * Places the hull on a heading outright, skipping the swing onto it. For boats whose
     * turning happened somewhere else — the server, for a networked ship — where running
     * TURN_RATE again locally would only fight the authority.
     */
    public set heading(radians: number) {
        this._heading = radians;
        this._targetHeading = radians;
        this._container.rotation = radians;
        this._nameplate.rotation = -radians;
    }

    /** Heading the boat is swinging onto. It gets there at TURN_RATE, never instantly. */
    public get targetHeading(): number {
        return this._targetHeading;
    }

    public set targetHeading(radians: number) {
        this._targetHeading = radians;
    }

    /**
     * Whether this hull has been abandoned — its captain disconnected and the ship kept sailing.
     * A look: the arena owns the fact (`Ship.ghost`) and everything about how a ghost behaves,
     * and all this does is draw it as one.
     *
     * Switched rather than eased, unlike the boost. A ghost is a **state change with a cause**,
     * not a thing a hull does more or less of, and the moment somebody's connection dies is
     * exactly the moment the other captains want to see it.
     */
    public set ghost(value: boolean) {
        if (value === this._ghost) return;

        this._ghost = value;
        this._container.tint = value ? GHOST_TINT : 0xffffff;
        this._container.alpha = value ? GHOST_ALPHA : 1;
    }

    /**
     * How far this hull has gone down: 0 is afloat, 1 is gone. The look of a **wreck**, which is
     * what the player's own boat becomes for the few seconds they are held at sea to watch it —
     * see DEATH_LINGER_SECONDS and `BattleScene._checkSunk`.
     *
     * The same wash a ghost gets, because it is saying a related thing and the sea has one colour
     * to say it in: this hull is no longer sailed. What differs is that it goes all the way. The
     * fade is `1 - t²` rather than `1 - t` so the wreck is still plainly there through the first
     * half of the count — a hull that faded evenly would be half gone before the player had
     * finished working out what happened — and then leaves quickly, so the screen change lands on
     * empty water rather than snatching a solid ship away.
     *
     * Eased rather than switched, unlike `ghost`, and that is the whole difference between them: a
     * ghost is a state somebody else's ship is in, and this is a thing happening to yours. The two
     * write the same two properties and the last assignment wins, which costs nothing because no
     * hull is ever both — a captain watching their own wreck is by definition still connected.
     */
    public set foundered(value: number) {
        const t = Math.min(1, Math.max(0, value));
        this._container.tint = GHOST_TINT;
        this._container.alpha = 1 - t * t;
    }

    public get speed(): number {
        return this._speed;
    }

    public set speed(pixelsPerSecond: number) {
        this._speed = pixelsPerSecond;
    }

    /** What this boat currently is. Assigning a different one refits it where it floats. */
    public get fitout(): Fitout {
        return this._fitout;
    }

    public set fitout(value: Fitout) {
        if (value === this._fitout) return;

        this._fitout = value;
        this._roster = rosterFor(value);
        this._deck.container.destroy({ children: true });
        this._deck = buildDeck(value);
        // Where the old deck was: above the shadow, below the flashes and the label.
        this._container.addChildAt(this._deck.container, 1);

        this._sizeToHull();
    }

    /** Where the boat is headed, in pixels per second. Heading 0 is -y, hence the signs. */
    public get velocity(): { x: number; y: number } {
        return { x: Math.sin(this._heading) * this._speed, y: -Math.cos(this._heading) * this._speed };
    }

    /** The label under the hull. Empty hides it — the local player does not need naming. */
    public set name(value: string) {
        this._nameplate.text = value;
        this._nameplate.visible = value.length > 0;
    }

    constructor(fitout: Fitout = STARTING_FITOUT) {
        this._fitout = fitout;
        this._roster = rosterFor(fitout);
        this._deck = buildDeck(fitout);

        // Counter-rotated in `update` so it stays upright while the hull comes about —
        // a name that turns with the ship is unreadable at exactly the moment you need it.
        this._nameplate = new Text({
            text: "",
            anchor: { x: 0.5, y: 0 },
            visible: false,
            style: {
                fontFamily: "Grobold",
                fontSize: NAME_SIZE,
                fill: 0xf2e2c4,
                stroke: { color: 0x0b2a35, width: 4, join: "round" }
            }
        });

        // The deck first; the flashes over the guns that make them; the label over everything.
        // `fitout`'s setter re-inserts a new deck at index 0, so this order is load-bearing.
        this._container.addChild(this._shadow, this._deck.container, this._flashes.container, this._nameplate);
        this._sizeToHull();
    }

    /**
     * Swings this boat's guns a step toward a **world** direction, and draws them where they got
     * to. Every hull in the sea gets this every frame: the local one off the player's pointer, a
     * rival's off `Ship.aim` on the wire.
     *
     * The heading is taken back out here rather than by the caller, because a gun's arc is
     * authored against the bow (`Slot.rotation`) and the boat is the thing that knows which way
     * its bow is pointing. The server does the identical sum on the identical aim, so the barrel
     * drawn here is the barrel a ball leaves — see shared/Aiming.ts and design/Controls.md.
     */
    public aimAt(worldHeading: number, deltaSeconds: number): void {
        this._battery.train(this._roster, shortestAngle(this._heading, worldHeading), deltaSeconds);
        train(this._deck, this._battery);
    }

    /**
     * Braces this boat's canvas round to the wind and says how hard it is pulling — the sails'
     * half of what `aimAt` does for the guns, and called the same way: every hull in the sea, every
     * frame, off the one wind the arena publishes.
     *
     * **The yards turn by half the angle off the wind.** With `theta = shortestAngle(wind,
     * heading)`, the brace is `theta / 2`: squared across the beam running dead downwind, round to
     * 45 degrees on a beam reach, and fore-and-aft with the hull in the eye of the wind. That is
     * how a square rig is actually worked, and it falls out of one division — no gybe to detect and
     * no side to remember, because the two ends of the range (+/-90 degrees) are the same picture
     * mirrored, and the hull is head to wind and luffing at the moment they meet.
     *
     * `wind` of `null` is a caller with no weather — the attract-screen boat — and leaves the
     * canvas squared and drawing, which is how it has always been drawn.
     */
    public trimTo(wind: number | null, deltaSeconds: number): void {
        const wanted = wind === null ? 0 : shortestAngle(wind, this._heading) / 2;
        const delta = shortestAngle(this._brace, wanted);

        this._brace = normaliseAngle(this._brace + Math.sign(delta) * Math.min(Math.abs(delta), BRACE_RATE * deltaSeconds));
        trim(this._deck, this._brace, wind === null ? 1 : sailPower(wind, this._heading));
    }

    /**
     * Sits this hull in the water: lifted on a crest, rolled by the slope under its beam, and
     * casting a shadow that lengthens as it drops into a trough.
     *
     * All three come out of `waveHeightAt` and `waveSlopeAt` at the boat's **offset from the
     * camera** — the same field the shader is drawing, which is what puts a hull on the crest it
     * looks like it is on. The sea is this client's own picture (src/scripts/sea/Waves.ts), so two
     * players may see a boat pitching slightly differently; that is the bargain the wake and the
     * school have always made.
     *
     * It is drawing and only drawing, and that is the **whole** of what the water does to a boat
     * (design/Water.md). Nothing here moves the hull, and nothing else
     * does either: the wind is the only thing that decides where a ship gets to.
     *
     * The lift and the roll go on the **deck**, not on the whole boat, so the nameplate neither
     * grows nor tilts with the sea. One consequence worth knowing: a gun is drawn inside that
     * rolled deck while its bearing is computed outside it, so a barrel can be drawn up to ROLL
     * off the line its ball actually leaves on. That is a couple of degrees, well inside the drift
     * design/Networking.md already accepts between a drawn gun and a fired one.
     */
    public ride(sea: Sea, offsetX: number, offsetY: number): void {
        const height = waveHeightAt(sea, offsetX, offsetY) * WAVE_HEIGHT;
        // Across the beam, which is a quarter turn off the bow — the slope a hull actually heels
        // to. Normalised so a full-strength face is about one, and ROLL reads as plain radians.
        const heel = waveSlopeAt(sea, offsetX, offsetY, this._heading + Math.PI / 2) * SLOPE_UNIT * WAVE_HEIGHT;

        this._deck.container.scale.set(1 + height * LIFT);
        this._deck.container.rotation = heel * ROLL;

        // Away from the light, and further away the lower the hull sits. The offset is worked out
        // in screen space and then turned back by the heading, because the shadow is a child of a
        // container the hull's course has already rotated — the light does not turn with the ship.
        const drop = (1 - height) / 2;
        const reach = this._fitout.size.length * SHADOW_REACH * drop;
        const sin = Math.sin(this._heading);
        const cos = Math.cos(this._heading);
        const awayX = -SEA_LIGHT.x * reach;
        const awayY = -SEA_LIGHT.y * reach;

        this._shadow.position.set(awayX * cos - awayY * sin, awayX * sin + awayY * cos);
        this._shadow.alpha = SHADOW_ALPHA_TROUGH + (SHADOW_ALPHA_CREST - SHADOW_ALPHA_TROUGH) * (1 - drop);
    }

    /** Where this boat's guns are, for whoever has to pick one to fire. See shared/Aiming.ts. */
    public get battery(): Battery {
        return this._battery;
    }

    /**
     * What one shot looks like on this screen: the gun thrown back on its mount, and fire out of
     * the muzzle. Driven by the arena's `shot` broadcast rather than by the press that caused it
     * — the recoil, the flash and the ball are one event, so they start together on every screen,
     * the firing player's included.
     *
     * `shotId` seeds which flash comes up, so every client shows the same flame for the same bang
     * without a byte on the wire. See MuzzleFlashes.ts.
     *
     * A slot this boat has no gun in is ignored on the way through `Battery` and lights nothing,
     * which is the right answer for a broadcast that arrives a frame after an upgrade re-plumbed
     * the deck.
     */
    public fired(slot: number, shotId: number): void {
        this._battery.kick(slot);

        const gun = this._deck.guns.find((candidate) => candidate.index === slot);
        const cannon = this._roster.find((candidate) => candidate.index === slot);
        if (!gun || !cannon) return;

        // Where the ball leaves, in the deck's own space. The same two offsets `muzzle()` stacks
        // on the server, minus the hull's heading — the boat container is already turned by it.
        // The gun's **rest** position, not the recoiled one: the ball spawns from the rest point
        // too, and the fire has to come out of the same hole as the ball. See shared/Gunnery.ts.
        const bearing = gun.rest + this._battery.offsetAt(slot);
        const sin = Math.sin(bearing);
        const cos = Math.cos(bearing);

        this._flashes.fire(
            gun.restX + cannon.muzzleAcross * cos - cannon.muzzleAlong * sin,
            gun.restY + cannon.muzzleAcross * sin + cannon.muzzleAlong * cos,
            bearing,
            cannon.drawn,
            shotId
        );
    }

    /** This boat's guns as the simulation reads them, rebuilt with its fitout. */
    public get roster(): readonly Cannon[] {
        return this._roster;
    }

    public update(deltaSeconds: number): void {
        this._advanceSails(deltaSeconds);
        this._flashes.update(deltaSeconds);

        this._heading = turnToward(this._heading, this._targetHeading, deltaSeconds, this._fitout.turn);
        this._container.rotation = this._heading;
        this._nameplate.rotation = -this._heading;
    }

    public destroy(): void {
        this._container.destroy({ children: true });
    }

    /**
     * Puts the nameplate where this hull's size says it goes — measured in hull lengths rather
     * than in pixels, so a Galleon's name still clears a Galleon's stern. See design/Ships.md.
     */
    private _sizeToHull(): void {
        this._nameplate.y = this._fitout.size.length * NAME_OFFSET_FACTOR;
        // Wider than the hull on both axes, so what shows past the deck is the soft part of the
        // blot rather than its edge. Beam first: the blot is round and the ship is not.
        this._shadow.setSize(this._fitout.size.beam * SHADOW_SPREAD, this._fitout.size.length * SHADOW_SPREAD);
    }

    /**
     * Billows the canvas at SAIL_FPS — and anything else on the deck whose art moves — off the
     * scene's own clock. One accumulator for the whole rig: the sails on a hull are in one wind,
     * and canvas cycling out of step reads as flapping rather than as a ship under way.
     */
    private _advanceSails(deltaSeconds: number): void {
        const animated = this._deck.animated;
        if (animated.length === 0) return;

        const loopSeconds = animated[0].totalFrames / SAIL_FPS;
        // Wrapped so the accumulator stays small no matter how long the scene has been up.
        this._sailTime = (this._sailTime + deltaSeconds) % loopSeconds;

        const frame = Math.floor(this._sailTime * SAIL_FPS);
        for (const sail of animated) sail.currentFrame = frame % sail.totalFrames;

        // The cloth just changed shape, so anything painted on it has to be re-fitted. Done here
        // as well as in `trim` because the attract screen advances canvas without trimming it.
        dressEmblems(this._deck);
    }

}
